import React, { useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";

const PUB = process.env.PUBLIC_URL || "";

const lvl = (levelRef, state, t) => Math.max(levelRef?.current || 0, 0.05 + Math.abs(Math.sin(t * 1.1)) * (state === "idle" ? 0.06 : 0.03));

/* Star constellation profile: sparse stars sampled from the head + nearest-neighbour star lines */
export function Constellation({ colorHex, state, levelRef, gazeRef }) {
  const group = useRef(), mat = useRef(), lmat = useRef();
  const gltf = useLoader(GLTFLoader, `${PUB}/nexus/head.glb`);
  const { stars, lines } = useMemo(() => {
    let mesh = null;
    gltf.scene.traverse((o) => { if (o.isMesh && !mesh) mesh = o; });
    const sampler = new MeshSurfaceSampler(new THREE.Mesh(mesh.geometry)).build();
    const N = 520, pts = [], v = new THREE.Vector3();
    for (let i = 0; i < N; i++) { sampler.sample(v); pts.push(v.clone()); }
    const sg = new THREE.BufferGeometry().setFromPoints(pts);
    const lp = [];
    pts.forEach((p, i) => {
      const near = pts.map((q, j) => [p.distanceToSquared(q), j]).filter(([, j]) => j > i).sort((a, b) => a[0] - b[0]).slice(0, 2);
      near.forEach(([d, j]) => { if (d < 0.09) lp.push(p, pts[j]); });
    });
    return { stars: sg, lines: new THREE.BufferGeometry().setFromPoints(lp) };
  }, [gltf]);
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime, a = lvl(levelRef, state, t);
    const g = gazeRef?.current, track = g && g.present && Date.now() - g.at < 4000;
    if (group.current) {
      const ty = track ? g.x * 0.5 : -0.6 + Math.sin(t * 0.12) * 0.25;
      group.current.rotation.y += (ty - group.current.rotation.y) * Math.min(1, dt * 2);
      group.current.position.y = -0.1 + Math.sin(t * 0.5) * 0.05;
    }
    if (mat.current) mat.current.size = 0.05 + (state === "speaking" ? a * 0.09 : 0) + Math.sin(t * 3) * 0.004;
    if (lmat.current) lmat.current.opacity = 0.18 + (state === "speaking" ? a * 0.6 : state === "listening" ? 0.25 : 0.05 * Math.sin(t));
  });
  return (
    <group ref={group} scale={0.42}>
      <points geometry={stars}><pointsMaterial ref={mat} color={colorHex} size={0.05} sizeAttenuation transparent opacity={0.95} depthWrite={false} blending={THREE.AdditiveBlending} /></points>
      <lineSegments geometry={lines}><lineBasicMaterial ref={lmat} color={colorHex} transparent opacity={0.2} depthWrite={false} blending={THREE.AdditiveBlending} /></lineSegments>
    </group>
  );
}

/* Energy orb: wire icosahedron shell around a pulsing core */
export function EnergyOrb({ colorHex, state, levelRef }) {
  const shell = useRef(), core = useRef(), inner = useRef();
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime, a = lvl(levelRef, state, t);
    if (shell.current) { shell.current.rotation.y += dt * (state === "thinking" ? 0.8 : 0.2); shell.current.rotation.x += dt * 0.07; shell.current.scale.setScalar(1 + a * 0.25); }
    if (inner.current) { inner.current.rotation.y -= dt * 0.35; inner.current.rotation.z += dt * 0.15; }
    if (core.current) core.current.scale.setScalar(0.55 + a * 0.9 + Math.sin(t * 2) * 0.04);
  });
  return (
    <group>
      <mesh ref={shell}><icosahedronGeometry args={[1.7, 2]} /><meshBasicMaterial color={colorHex} wireframe transparent opacity={0.35} blending={THREE.AdditiveBlending} /></mesh>
      <mesh ref={inner}><icosahedronGeometry args={[1.15, 1]} /><meshBasicMaterial color={colorHex} wireframe transparent opacity={0.5} blending={THREE.AdditiveBlending} /></mesh>
      <mesh ref={core}><sphereGeometry args={[1, 48, 48]} /><meshBasicMaterial color={colorHex} transparent opacity={0.85} toneMapped={false} /></mesh>
    </group>
  );
}

/* Voice waveform: radial ring of bars driven by the voice level */
export function VoiceWave({ colorHex, state, levelRef }) {
  const N = 96, refs = useRef([]);
  const bars = useMemo(() => Array.from({ length: N }, (_, i) => (i / N) * Math.PI * 2), []);
  useFrame((st) => {
    const t = st.clock.elapsedTime, a = lvl(levelRef, state, t);
    refs.current.forEach((m, i) => {
      if (!m) return;
      const h = 0.15 + a * 2.2 * (0.5 + 0.5 * Math.sin(i * 0.7 + t * 9)) * (0.6 + 0.4 * Math.sin(i * 0.23 + t * 2));
      m.scale.y = h;
    });
  });
  return (
    <group>
      {bars.map((ang, i) => (
        <mesh key={i} ref={(el) => (refs.current[i] = el)} position={[Math.cos(ang) * 1.6, Math.sin(ang) * 1.6, 0]} rotation={[0, 0, ang - Math.PI / 2]}>
          <boxGeometry args={[0.045, 1, 0.045]} />
          <meshBasicMaterial color={i % 2 ? colorHex : "#E879F9"} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

/* Light iris: concentric rings that dilate when speaking and drift toward the viewer */
export function LightIris({ colorHex, state, levelRef, gazeRef }) {
  const group = useRef(), pupil = useRef(), rings = useRef([]);
  const R = [0.5, 0.75, 1.0, 1.3, 1.65, 2.0];
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime, a = lvl(levelRef, state, t);
    const g = gazeRef?.current, track = g && g.present && Date.now() - g.at < 4000;
    if (group.current) {
      group.current.rotation.y += ((track ? g.x * 0.4 : Math.sin(t * 0.3) * 0.2) - group.current.rotation.y) * Math.min(1, dt * 2);
      group.current.rotation.x += ((track ? -g.y * 0.3 : 0) - group.current.rotation.x) * Math.min(1, dt * 2);
    }
    rings.current.forEach((m, i) => { if (m) { m.rotation.z += dt * (i % 2 ? 0.2 : -0.15) * (1 + a * 3); m.scale.setScalar(1 + a * 0.12 * (i + 1) / R.length); } });
    if (pupil.current) pupil.current.scale.setScalar(0.35 + (state === "speaking" ? a * 0.5 : 0) + Math.sin(t * 1.4) * 0.02);
  });
  return (
    <group ref={group}>
      {R.map((r, i) => (
        <mesh key={r} ref={(el) => (rings.current[i] = el)}>
          <torusGeometry args={[r, i === 2 ? 0.03 : 0.012, 12, 160, i % 2 ? Math.PI * 1.6 : Math.PI * 2]} />
          <meshBasicMaterial color={i === 3 ? "#FACC15" : colorHex} transparent opacity={0.75} toneMapped={false} blending={THREE.AdditiveBlending} />
        </mesh>
      ))}
      <mesh ref={pupil}><circleGeometry args={[1, 64]} /><meshBasicMaterial color={colorHex} transparent opacity={0.9} toneMapped={false} /></mesh>
    </group>
  );
}

/* Neuron brain: blue two-hemisphere neural net; signals fire and travel along synapses (more when Nexus speaks) */
export function NeuronBrain({ colorHex, state, levelRef, gazeRef }) {
  const group = useRef(), act = useRef(null);
  const { pts, lines, nbr, pc, lc, idx } = useMemo(() => {
    const N = 950, p = [], rnd = (a, b) => a + Math.random() * (b - a);
    while (p.length < N) {
      const side = p.length % 2 ? 1 : -1, u = rnd(0, Math.PI * 2), v = Math.acos(rnd(-1, 1)), r = Math.cbrt(rnd(0.55, 1));
      let x = Math.sin(v) * Math.cos(u), y = Math.sin(v) * Math.sin(u), z = Math.cos(v);
      // side view: x = front-back (long), y = height, z = left/right hemispheres split by a fissure
      const zz = side * (0.1 + Math.abs(z) * 0.72), xx = x * 1.3, yy = y * 0.82 + 0.05 * Math.sin(xx * 10 + zz * 6);
      if (yy < -0.45 && xx > -0.2 && xx < 0.55) continue; // notch under temporal lobe
      x = xx; y = yy < -0.6 ? -0.6 : yy; z = zz;
      p.push(new THREE.Vector3(x * r * 1.5, y * r * 1.5, z * r * 1.5));
    }
    const nb = p.map(() => []), seg = [], si = [];
    p.forEach((a, i) => {
      p.map((q, j) => [a.distanceToSquared(q), j]).filter(([d, j]) => j !== i && d < 0.075).sort((x, y) => x[0] - y[0]).slice(0, 3)
        .forEach(([, j]) => { nb[i].push(j); if (j > i) { seg.push(a, p[j]); si.push([i, j]); } });
    });
    const g = new THREE.BufferGeometry().setFromPoints(p), l = new THREE.BufferGeometry().setFromPoints(seg);
    const pc_ = new Float32Array(p.length * 3), lc_ = new Float32Array(seg.length * 3);
    g.setAttribute("color", new THREE.BufferAttribute(pc_, 3)); l.setAttribute("color", new THREE.BufferAttribute(lc_, 3));
    return { pts: g, lines: l, nbr: nb, pc: pc_, lc: lc_, idx: si };
  }, []);
  const base = useMemo(() => new THREE.Color(colorHex), [colorHex]);
  useFrame((st, dt) => {
    const t = st.clock.elapsedTime, a = lvl(levelRef, state, t), n = nbr.length;
    if (!act.current) act.current = new Float32Array(n);
    const A = act.current, next = new Float32Array(n);
    const seeds = state === "speaking" ? 2 + a * 14 : state === "thinking" ? 5 : state === "listening" ? 3 : 1;
    for (let k = 0; k < seeds; k++) if (Math.random() < 0.6) A[(Math.random() * n) | 0] = 1;
    for (let i = 0; i < n; i++) {
      next[i] = Math.max(next[i], A[i] * Math.pow(0.04, dt));
      if (A[i] > 0.85) nbr[i].forEach((j) => { if (A[j] < 0.2 && Math.random() < 0.55) next[j] = Math.max(next[j], 0.95); });
    }
    act.current = next;
    for (let i = 0; i < n; i++) {
      const v = next[i];
      pc[i * 3] = base.r * 0.35 + v * 0.9; pc[i * 3 + 1] = base.g * 0.45 + v * 0.95; pc[i * 3 + 2] = base.b * 0.75 + v;
    }
    idx.forEach(([i, j], k) => {
      const v = Math.max(next[i], next[j]);
      for (let e = 0; e < 2; e++) { const o = (k * 2 + e) * 3; lc[o] = base.r * 0.12 + v * 0.7; lc[o + 1] = base.g * 0.2 + v * 0.85; lc[o + 2] = base.b * 0.4 + v; }
    });
    pts.attributes.color.needsUpdate = true; lines.attributes.color.needsUpdate = true;
    const g = gazeRef?.current, track = g && g.present && Date.now() - g.at < 4000;
    if (group.current) {
      const ty = (track ? g.x * 0.4 : Math.sin(t * 0.15) * 0.35) - 0.35;
      group.current.rotation.y += (ty - group.current.rotation.y) * Math.min(1, dt * 1.5);
      group.current.rotation.x = 0.15 + Math.sin(t * 0.4) * 0.04;
      group.current.scale.setScalar(1 + Math.sin(t * 1.2) * 0.015 + (state === "speaking" ? a * 0.05 : 0));
    }
  });
  return (
    <group ref={group}>
      <points geometry={pts}><pointsMaterial vertexColors size={0.055} sizeAttenuation transparent opacity={0.95} depthWrite={false} blending={THREE.AdditiveBlending} /></points>
      <lineSegments geometry={lines}><lineBasicMaterial vertexColors transparent opacity={0.85} depthWrite={false} blending={THREE.AdditiveBlending} /></lineSegments>
    </group>
  );
}

export const LOOKS = [
  ["neuron", "Neuron Brain"],
  ["constellation", "Constellation"], ["brain", "Brain"], ["orb", "Energy Orb"],
  ["wave", "Voice Wave"], ["iris", "Light Iris"], ["face", "Holo Head"],
];
